import { API_config } from '../../config/apiConfig';

const apiUrl = API_config.apiUrl;
const authHeader = API_config.authHeader;

/**
 * Fetches all devices with their position data
 * Combines device data with position data similar to the old implementation
 * @returns {Promise<Array>} Array of devices with position data
 */
export async function getDeviceList() {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': authHeader
        };

        const devicesUrl = `${apiUrl}/devices`;
        const positionsUrl = `${apiUrl}/positions`;

        // Get the list of devices
        const deviceResponse = await fetch(devicesUrl, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader
            }
        });

        if (!deviceResponse.ok) {
            throw new Error('Failed to fetch devices');
        }

        const devices = await deviceResponse.json();

        // Fetch positions for each device and attach them to the device object
        const devicesWithPositionData = await Promise.all(devices.map(async (device) => {
            try {
                // Fetch all positions for each device using the device id
                const positionResponse = await fetch(`${positionsUrl}?deviceId=${device.id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': authHeader
                    }
                });

                if (!positionResponse.ok) {
                    return {
                        ...device,
                        PositionData: []
                    };
                }

                const positions = await positionResponse.json();

                // Combine device and all position data into one object under `PositionData`
                return {
                    ...device,
                    PositionData: positions
                };
            } catch (posError) {
                console.error(`Failed to fetch positions for device ${device.id}:`, posError.message);
                return {
                    ...device,
                    PositionData: []
                };
            }
        }));

        return devicesWithPositionData;
    } catch (error) {
        console.error('An error occurred fetching devices:', error.message);
        throw error;
    }
}

/**
 * Finds a device by its IMEI (uniqueId)
 * @param {string} imei - The IMEI/uniqueId to search for
 * @returns {Promise<number>} The device ID
 */
export async function getDeviceByImei(imei) {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': authHeader
        };

        const devicesUrl = `${apiUrl}/devices`;

        const response = await fetch(devicesUrl, { headers });

        if (!response.ok) {
            throw new Error('Failed to fetch devices');
        }

        const devices = await response.json();
        const device = devices.find(d => d.uniqueId === imei.toString());

        if (!device) {
            throw new Error('Device with this IMEI not found');
        }

        return device.id;
    } catch (error) {
        console.error('An error occurred:', error.message);
        throw error;
    }
}

/**
 * Updates a device's name (and optionally creates if not exists)
 * @param {number} deviceId - The device ID
 * @param {string} deviceName - The new device name
 * @param {string} deviceUniqueId - The device IMEI/uniqueId
 * @returns {Promise<Object>} The updated device object
 */
export async function updateDeviceName(deviceId, deviceName, deviceUniqueId) {
    const headers = {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
    };

    const deviceUrl = `${apiUrl}/devices/${deviceId}`;

    try {
        const response = await fetch(deviceUrl, { headers });

        if (response.status === 200) {
            const deviceData = await response.json();
            deviceData.name = deviceName;
            deviceData.uniqueId = deviceUniqueId;

            try {
                const updateResponse = await fetch(deviceUrl, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(deviceData)
                });

                if (!updateResponse.ok) {
                    throw new Error('Failed to update device');
                }

                const updatedDevice = await updateResponse.json();
                console.log('Device updated successfully:', updatedDevice);
                return updatedDevice;
            } catch (updateError) {
                console.error('Failed to update device:', updateError.message);
                throw updateError;
            }
        } else {
            throw new Error('Device not found');
        }
    } catch (error) {
        if (error.message === 'Device not found') {
            // If device doesn't exist, create it
            const newDeviceData = {
                id: deviceId,
                name: deviceName,
                uniqueId: deviceUniqueId
            };

            try {
                const createResponse = await fetch(`${apiUrl}/devices`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(newDeviceData)
                });

                if (!createResponse.ok) {
                    throw new Error('Failed to create device');
                }

                const createdDevice = await createResponse.json();
                console.log('Device created successfully:', createdDevice);
                return createdDevice;
            } catch (createError) {
                console.error('Failed to create device:', createError.message);
                throw createError;
            }
        } else {
            console.error('An error occurred:', error.message);
            throw error;
        }
    }
}

/**
 * Updates a specific attribute of a device
 * @param {number} deviceId - The device ID
 * @param {string} attributeKey - The key of the attribute to update
 * @param {any} attributeValue - The value of the attribute
 * @returns {Promise<Object>} The updated device object
 */
export async function updateDeviceAttribute(deviceId, attributeKey, attributeValue) {
    const headers = {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
    };

    const deviceUrl = `${apiUrl}/devices/${deviceId}`;

    try {
        // First fetch the current device
        const response = await fetch(deviceUrl, { headers });

        if (response.status === 200) {
            const deviceData = await response.json();

            // Update or initialize attributes
            deviceData.attributes = deviceData.attributes || {};
            deviceData.attributes[attributeKey] = attributeValue;

            try {
                // Send the updated device back
                const updateResponse = await fetch(deviceUrl, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(deviceData)
                });

                if (!updateResponse.ok) {
                    throw new Error(`Failed to update device attribute ${attributeKey}`);
                }

                const updatedDevice = await updateResponse.json();
                console.log(`Device attribute ${attributeKey} updated successfully:`, updatedDevice);
                return updatedDevice;
            } catch (updateError) {
                console.error(`Failed to update device attribute ${attributeKey}:`, updateError.message);
                throw updateError;
            }
        } else {
            throw new Error('Device not found');
        }
    } catch (error) {
        console.error('An error occurred while updating device attribute:', error.message);
        throw error;
    }
}

/**
 * Loads device names from the ro_entry.txt file
 * @returns {Promise<Array>} Array of device name strings
 */
export async function loadDeviceNames() {
    try {
        // Use PUBLIC_URL to handle different base paths in dev and production
        const publicUrl = process.env.PUBLIC_URL || '';
        const response = await fetch(`${publicUrl}/data/ro_entry.txt`);

        if (!response.ok) {
            throw new Error('Failed to load device names file');
        }

        const text = await response.text();
        const lines = text.split('\n').filter(line => line.trim() !== '');

        return lines;
    } catch (error) {
        console.error('Error loading device names:', error);
    }
}

/**
 * Fetches positions for a specific device
 * @param {number} deviceId - The device ID
 * @param {Object} options - Optional query params like from, to, limit
 * @returns {Promise<Array>} Array of positions
 */
export async function getPositions(deviceId, options = {}) {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': authHeader
        };

        const positionsUrl = `${apiUrl}/positions`;
        // Build query string
        const params = new URLSearchParams({ deviceId, ...options });

        const response = await fetch(`${positionsUrl}?${params.toString()}`, { headers });

        if (!response.ok) {
            throw new Error('Failed to fetch positions');
        }

        return await response.json();
    } catch (error) {
        console.error(`Failed to fetch positions for device ${deviceId}:`, error.message);
        throw error;
    }
}

/**
 * Fetches the entry list
 * @returns {Promise<Array>} Array of entry objects
 */
export async function getEntryList() {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': API_config.bearerAuthHeader
        };

        const response = await fetch(API_config.entryListApiUrl, { headers });

        if (!response.ok) {
            throw new Error('Failed to fetch entry list');
        }

        const result = await response.json();
        return result.data || [];
    } catch (error) {
        console.error('Failed to fetch entry list:', error.message);
        throw error;
    }
}

/**
 * Uploads (imports) a new entry list, replacing existing ones
 * @param {Array} entries - Array of objects parsed from Excel
 * @returns {Promise<Object>} Response object
 */
export async function uploadEntryList(entries) {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': API_config.bearerAuthHeader
        };

        const payload = {
            entries,
            clear_existing: true
        };

        const response = await fetch(API_config.entryListApiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error('Failed to upload entry list');
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to upload entry list:', error.message);
        throw error;
    }
}

/**
 * Clears the entry list
 * @returns {Promise<Object>} Response object
 */
export async function clearEntryList() {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': API_config.bearerAuthHeader
        };

        const response = await fetch(API_config.entryListApiUrl, {
            method: 'DELETE',
            headers
        });

        if (!response.ok) {
            throw new Error('Failed to clear entry list');
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to clear entry list:', error.message);
        throw error;
    }
}

/**
 * Fetches all groups from Traccar
 * @returns {Promise<Array>} Array of group objects
 */
export async function getGroups() {
    try {
        const bulkAuthHeader = 'Basic ' + window.btoa('all@monitgps.pl:@llMonitgGPS');
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': bulkAuthHeader
        };

        const response = await fetch(`${apiUrl}/groups?all=true`, { headers });
        if (!response.ok) {
            const text = await response.text();
            console.error(`getGroups failed: ${response.status}`, text);
            throw new Error(`Failed to fetch groups (${response.status})`);
        }
        return await response.json();
    } catch (error) {
        console.error('An error occurred fetching groups:', error.message);
        throw error;
    }
}

/**
 * Fetches all devices directly without fetching positions (for bulk operations)
 * @returns {Promise<Array>} Array of raw device objects
 */
export async function getRawDevices() {
    try {
        const bulkAuthHeader = 'Basic ' + window.btoa('all@monitgps.pl:@llMonitgGPS');
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': bulkAuthHeader
        };

        const response = await fetch(`${apiUrl}/devices?all=true`, { headers });
        if (!response.ok) {
            const text = await response.text();
            console.error(`getRawDevices failed: ${response.status}`, text);
            throw new Error(`Failed to fetch raw devices (${response.status})`);
        }
        return await response.json();
    } catch (error) {
        console.error('An error occurred fetching raw devices:', error.message);
        throw error;
    }
}

/**
 * Updates a device directly with a completely replaced payload object
 * @param {Object} deviceData - The updated device payload
 * @returns {Promise<Object>} The updated device object
 */
export async function updateDeviceRaw(deviceData) {
    try {
        const bulkAuthHeader = 'Basic ' + window.btoa('all@monitgps.pl:@llMonitgGPS');
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': bulkAuthHeader
        };

        const response = await fetch(`${apiUrl}/devices/${deviceData.id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(deviceData)
        });

        if (!response.ok) {
            throw new Error(`Failed to update raw device ${deviceData.id}`);
        }
        return await response.json();
    } catch (error) {
        console.error(`An error occurred updating raw device ${deviceData?.id}:`, error.message);
        throw error;
    }
}
